import { useMemo } from "react";
import type { Revelation, WorldState } from "../engine/types";
import { deriveAggregates } from "../engine/types";
import { SUPPORT_URL } from "../support";
import { MetaBar } from "./MetaBar";
import { IndicatorStrip } from "./IndicatorStrip";
import { PoliticalProfileCard } from "./PoliticalProfileCard";
import { TurnReportPanel } from "./TurnReportPanel";
import { OrderMeter } from "./OrderMeter";
import { OpportunityBanner } from "./OpportunityBanner";
import { ProblemZone } from "./ProblemZone";
import { DrawerPanels } from "./DrawerPanels";
import { PressTicker } from "./PressTicker";
import { IndicatorDetailSheet } from "./IndicatorDetailSheet";
import { DelayedEffectsQueue } from "./DelayedEffectsQueue";
import { PledgeOverlay } from "./PledgeOverlay";
import { BetaBadge } from "./BetaBadge";
import { FeedbackModal } from "./FeedbackModal";
import { PollOverlay } from "./PollOverlay";
import { ProfileCrystalOverlay } from "./ProfileCrystalOverlay";
import { ProfileMidterm } from "./ProfileMidterm";
import { MonthSweep } from "./MonthSweep";
import { MandateEndOverlay } from "./MandateEndOverlay";
import { CheckpointBanner } from "./CheckpointBanner";
import { TutorialOverlay } from "./TutorialOverlay";
import { useGame } from "../store/useGame";

import type { HeadlineEntry } from "../store/useGame";

interface Props {
  state: WorldState;
  headlines: HeadlineEntry[];
  lastRevelations: Revelation[];
}

/**
 * Bannière d'erreur de tour (robustesse 2026-06-10). Un panic moteur ne
 * tue plus l'UI : l'état affiché reste le dernier tour valide, la save
 * serveur est intacte, et on guide vers le rechargement.
 */
function TurnErrorBanner() {
  const turnError = useGame((s) => s.turnError);
  const dismiss = useGame((s) => s.dismissTurnError);
  if (!turnError) return null;
  return (
    <div className="turn-error" role="alert">
      <strong>⚠ Le tour n'a pas pu être joué.</strong> {turnError} — votre
      partie est sauvegardée,{" "}
      <button
        type="button"
        className="turn-error-reload"
        onClick={() => window.location.reload()}
      >
        recharger la page
      </button>{" "}
      la restaurera.
      <button
        type="button"
        className="turn-error-dismiss"
        onClick={dismiss}
        aria-label="Fermer l'alerte"
      >
        ×
      </button>
    </div>
  );
}

export function Cockpit({ state, headlines, lastRevelations }: Props) {
  const aggregates = useMemo(() => deriveAggregates(state), [state]);
  const hasOffer = useGame((s) => s.currentDecision() !== null);
  const skipTurn = useGame((s) => s.skipTurn);
  const resetGame = useGame((s) => s.resetGame);
  const activeIndicatorId = useGame((s) => s.activeIndicatorId);

  return (
    <div className="cockpit">
      <MetaBar
        state={state}
        aggregates={aggregates}
        onNextTurn={skipTurn}
        allowSkip={!hasOffer && !state.polity.mandate_ended}
      />
      <IndicatorStrip state={state} aggregates={aggregates} />
      <PoliticalProfileCard state={state} />
      <div className="report-zone">
        <OrderMeter />
        <TurnReportPanel />
        <TurnErrorBanner />
      </div>

      <div className="problem-region">
        <OpportunityBanner />
        <ProblemZone state={state} />
        {lastRevelations.length > 0 && (
          <div className="revelations-stack">
            {lastRevelations.map((r, i) => (
              <div key={i} className="revelations-banner">
                <strong>⊕ RÉVÉLATION</strong> — {r.narrative}
              </div>
            ))}
          </div>
        )}
        <DelayedEffectsQueue />
      </div>

      <PressTicker
        headlines={headlines}
        state={state}
        aggregates={aggregates}
      />

      <DrawerPanels state={state} aggregates={aggregates} />

      <footer className="app-footer">
        {/* Accroche de pied — la maquette ferme la page sur l'enjeu plutôt
            que sur un colophon (2026-08-06). Le décompte vient de l'état,
            pas d'un texte figé. */}
        <div className="app-footer-hook">Le mandat n'attend pas.</div>
        <div className="app-footer-hook-sub">
          {Math.max(0, 60 - state.turn)} mois vous restent. Le pays ne se
          souvient que du dernier.
        </div>
        <div className="rule-dotted app-footer-rule" aria-hidden="true" />
        <button
          type="button"
          className="app-footer-reset"
          onClick={() => {
            if (
              window.confirm(
                "Effacer la sauvegarde et recommencer une nouvelle partie ?",
              )
            ) {
              resetGame();
            }
          }}
        >
          ↺ Nouvelle partie
        </button>
        {SUPPORT_URL && (
          <a
            className="app-footer-support"
            href={SUPPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            ♥ Soutenir le projet
          </a>
        )}
      </footer>

      {activeIndicatorId && (
        <IndicatorDetailSheet
          indicatorId={activeIndicatorId}
          state={state}
          aggregates={aggregates}
        />
      )}

      <PledgeOverlay />
      <PollOverlay />
      <ProfileCrystalOverlay />
      <ProfileMidterm />
      <MonthSweep />
      <CheckpointBanner />
      <MandateEndOverlay />
      <TutorialOverlay turn={state.turn} />

      <BetaBadge />
      <FeedbackModal />
    </div>
  );
}
